/**
 * ChildAvatarStrip — horizontal row of child avatars under the TopNav.
 *
 * Per founder spec (2026-05): "For parents show blocks for each child
 * avatar even if it's one. So they can easily switch children's board."
 *
 * Renders only when the user is signed in AND has at least one child
 * profile. Clicking an avatar:
 *   1. Sets it as the selected board (localStorage)
 *   2. Navigates to /board/:id (or stays on /pick-board if already there)
 *
 * The "+ Add another" tile leads to /create-board (the wizard, Phase 2).
 *
 * AI photo generator usage is reserved for the avatar field via the
 * existing /api/image/generate-tile endpoint — but only inside the
 * avatar editor (not added here so we don't widen the surface).
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { setSelectedBoardId, useSelectedBoard } from "@/lib/selectedBoard";

const HIDDEN_PATHS = new Set([
  "/", "/login", "/register", "/forgot-password", "/reset-password",
  "/pair-device", "/consent/parental/verify", "/billing/success",
  "/about", "/community", "/press", "/download", "/pricing",
  "/therapists", "/built-in-a-day", "/demo-safety", "/waitlist", "/whats-new",
  "/about-us", "/contact", "/security", "/data-protection", "/learn-and-grow",
  "/create-board",
]);

export default function ChildAvatarStrip() {
  const { user } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [selectedId] = useSelectedBoard();
  const [boards, setBoards] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/profiles")
      .then((r) => setBoards(r.data?.profiles || r.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [user]);

  if (!user) return null;
  if (HIDDEN_PATHS.has(location.pathname)) return null;
  if (location.pathname.startsWith("/board/")) return null;
  if (location.pathname.startsWith("/first-then/") || location.pathname.startsWith("/choice/")) return null;
  if (location.pathname.startsWith("/admin/")) return null;
  if (!loaded || boards.length === 0) return null;

  const pick = (b) => {
    setSelectedBoardId(b.id);
    nav(`/board/${b.id}`);
  };

  return (
    <div
      className="bg-[color:var(--cream)] border-b border-[color:var(--ink)]/15"
      data-testid="child-avatar-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 overflow-x-auto">
        <span className="text-xs uppercase tracking-[0.16em] font-bold text-[color:var(--ink)]/60 shrink-0">
          Children
        </span>
        {boards.map((b) => {
          const active = selectedId === b.id;
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => pick(b)}
              className={`group inline-flex items-center gap-2 px-2 py-1 rounded-md transition shrink-0 ${
                active
                  ? "bg-[color:var(--gold)] border-2 border-[color:var(--ink)]"
                  : "border-2 border-transparent hover:border-[color:var(--ink)]/30 hover:bg-white"
              }`}
              aria-current={active ? "true" : undefined}
              data-testid={`child-avatar-${b.id}`}
            >
              <span
                className="w-9 h-9 rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center text-lg overflow-hidden"
                style={{ background: b.color || "#FFB627" }}
                aria-hidden
              >
                {b.avatar_url ? (
                  <img src={b.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span>{b.avatar || "🌟"}</span>
                )}
              </span>
              <span className="text-sm font-semibold truncate max-w-[120px]">{b.name}</span>
            </button>
          );
        })}
        <Link
          to="/create-board"
          className="inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold text-[color:var(--ink)]/70 hover:text-[color:var(--ink)] shrink-0"
          data-testid="child-avatar-add"
        >
          <Plus size={14} aria-hidden />
          Add another
        </Link>
      </div>
    </div>
  );
}
